import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { PageHeader, Segmented } from '../components/ds';
import { Trophy, Medal, RefreshCw, Lock, Unlock, TrendingUp, Loader2, Crown } from 'lucide-react';

const fmtEur = (n) => {
  if (n === null || n === undefined) return '—';
  return `${Math.round(Number(n)).toLocaleString('fr-FR')} €`;
};

const PERIODS = [
  { key: 'month', label: 'Mois' },
  { key: 'quarter', label: 'Trimestre' },
  { key: 'semester', label: 'Semestre' },
  { key: 'year', label: 'Année' },
  { key: 'all', label: 'Tout' },
];

const ALLOWED = ['super_admin', 'admin', 'commercial', 'commerciale'];
const PODIUM = [Crown, Medal, Medal];

export default function PerformanceCommerciauxPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sensitive (primes) gate
  const [pin, setPin] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [primes, setPrimes] = useState(null);

  const allowed = ALLOWED.includes(user?.role);

  useEffect(() => {
    if (!allowed) {
      navigate('/dashboard');
      return;
    }
    fetchData(period);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async (p) => {
    setLoading(true);
    try {
      const res = await api().get(`/performance/leaderboard?period=${p}`);
      setData(res.data);
    } catch (e) {
      toast.error("Impossible de charger le classement");
    } finally {
      setLoading(false);
    }
  };

  const changePeriod = (p) => {
    setPeriod(p);
    fetchData(p);
    if (primes) unlockPrimes(p); // keep primes in sync if already unlocked
  };

  const unlockPrimes = async (p = period, code = pin) => {
    if (!code) {
      toast.error('Entrez le code');
      return;
    }
    setUnlocking(true);
    try {
      const res = await api().post('/performance/primes', { pin: code, period: p });
      setPrimes(res.data);
    } catch (e) {
      if (e?.response?.status === 401) toast.error('Code incorrect');
      else toast.error("Impossible d'afficher les primes");
      setPrimes(null);
    } finally {
      setUnlocking(false);
    }
  };

  const rows = data?.leaderboard || [];
  const totals = data?.totals || {};
  const top3 = rows.slice(0, 3);
  const primeMap = {};
  (primes?.primes || []).forEach((p) => { primeMap[p.user_id] = p; });

  const th = 'font-bold uppercase text-[11px] tracking-[0.1em] text-label px-4 py-3';

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="performance-page">
        {/* En-tête */}
        <PageHeader overline="Commerce · Classement de l'équipe commerciale" title="PERFORMANCE COMMERCIAUX">
          <Segmented value={period} onChange={changePeriod} options={PERIODS.map(p => ({ value: p.key, label: p.label }))} />
          <button onClick={() => fetchData(period)} disabled={loading} aria-label="Rafraîchir" className="h-10 w-10 inline-flex items-center justify-center rounded-ctrl bg-surface border border-border text-ink hover:bg-surface-2" data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </PageHeader>

        {/* Bandeau scène graphite §6.5 */}
        <div className="rounded-scene bg-graphite-900 p-6 grid grid-cols-2 md:grid-cols-4">
          {[
            { label: 'Commerciaux', value: totals.commercials ?? 0 },
            { label: 'Dossiers signés', value: totals.signed ?? 0 },
            { label: 'CA signé (équipe)', value: fmtEur(totals.ca_signed) },
            { label: 'CAPEX engagé', value: fmtEur(totals.capex_signed) },
          ].map((c, i) => (
            <div key={i} className={`px-5 ${i > 0 ? 'md:border-l border-graphite-600' : ''}`}>
              <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-ondark-3 mb-1.5 truncate">{c.label}</p>
              <p className="font-display font-semibold text-white text-[32px] leading-none tabular-nums">{c.value}</p>
            </div>
          ))}
        </div>

        {/* Podium */}
        {top3.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="podium">
            {top3.map((r, idx) => {
              const Icon = PODIUM[idx];
              return (
                <div key={r.user_id} className="rounded-card bg-surface p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-ctrl flex items-center justify-center bg-graphite-900">
                      <Icon className="w-5 h-5 text-orange" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-display text-sm text-orange-text font-semibold">#{r.rank}</p>
                      <p className="font-semibold text-ink truncate">{r.name}</p>
                    </div>
                  </div>
                  <div className="mt-4 flex items-end justify-between">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.08em] text-label font-bold">CA signé</p>
                      <p className="font-display text-2xl font-semibold tabular-nums text-ink">{fmtEur(r.ca_signed)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-ink tabular-nums">{r.signed} signé{r.signed > 1 ? 's' : ''}</p>
                      <p className="text-xs text-text-3">{r.taux_reussite}% réussite</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Classement */}
        <div className="rounded-card bg-surface overflow-x-auto">
          <table className="w-full text-sm" data-testid="leaderboard-table">
            <thead>
              <tr className="border-b border-[#E6E4DE]">
                <th className={`text-left ${th}`}>Rang</th>
                <th className={`text-left ${th}`}>Commercial</th>
                <th className={`text-right ${th}`}>Créées</th>
                <th className={`text-right ${th}`}>GO</th>
                <th className={`text-right ${th}`}>Signés</th>
                <th className={`text-right ${th}`}>Réussite</th>
                <th className={`text-right ${th}`}>CA signé</th>
                <th className={`text-right ${th}`}>CAPEX</th>
                {primes && <th className={`text-right ${th}`}>Prime (1%)</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={primes ? 9 : 8} className="text-center py-10 text-text-3">Chargement…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={primes ? 9 : 8} className="text-center py-10">
                  <p className="text-text-3 mb-3">Aucune donnée sur cette période. Essayez Trimestre ou Année.</p>
                  <button onClick={() => changePeriod('year')} className="h-9 px-4 rounded-ctrl text-sm font-semibold bg-surface border border-border text-ink hover:bg-surface-2">Voir l'année</button>
                </td></tr>
              ) : rows.map((r) => (
                <tr key={r.user_id} className="border-b border-divider hover:bg-surface-2" data-testid={`row-${r.user_id}`}>
                  <td className="px-4 py-3">
                    <span className={`font-display inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold ${r.rank <= 3 ? 'text-orange-text' : 'bg-surface-2 text-text-2'}`}>{r.rank}</span>
                  </td>
                  <td className="px-4 py-3 font-medium text-ink">{r.name}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-text-2">{r.created}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-text-2">{r.go}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold text-ink">{r.signed}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-text-2">{r.taux_reussite}%</td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold text-ink">{fmtEur(r.ca_signed)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-text-2">{fmtEur(r.capex_signed)}</td>
                  {primes && (
                    <td className="px-4 py-3 text-right tabular-nums font-semibold text-orange-text" data-testid={`prime-${r.user_id}`}>
                      {fmtEur(primeMap[r.user_id]?.prime_totale)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Sensible : Primes (code) §6.5 — carte blanche icône cadenas */}
        <div className="rounded-card bg-surface p-5" data-testid="primes-section">
          {!primes ? (
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-3 flex-1">
                <div className="w-10 h-10 rounded-ctrl bg-surface-2 flex items-center justify-center">
                  <Lock className="w-5 h-5 text-text-2" />
                </div>
                <div>
                  <p className="font-semibold text-ink">Primes estimées (donnée sensible)</p>
                  <p className="text-sm text-text-3">Entrez le code d'accès pour afficher les primes par commercial.</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Input type="password" inputMode="numeric" placeholder="Code" value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') unlockPrimes(); }}
                  className="w-28" data-testid="pin-input" />
                <button onClick={() => unlockPrimes()} disabled={unlocking} className="h-11 px-4 inline-flex items-center justify-center gap-2 rounded-ctrl text-sm font-bold bg-orange text-orange-on hover:brightness-105 disabled:opacity-60" data-testid="pin-submit">
                  {unlocking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlock className="w-4 h-4" />}
                  Déverrouiller
                </button>
              </div>
            </div>
          ) : (
            <div data-testid="primes-unlocked">
              <div className="flex items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-2 text-ink">
                  <Unlock className="w-5 h-5 text-orange-text" />
                  <span className="font-display font-bold uppercase text-lg">Primes estimées</span>
                </div>
                <button onClick={() => { setPrimes(null); setPin(''); }} className="h-9 px-3 inline-flex items-center rounded-ctrl text-sm font-semibold bg-surface border border-border text-ink hover:bg-surface-2" data-testid="primes-lock">
                  <Lock className="w-4 h-4 mr-1" /> Masquer
                </button>
              </div>
              <p className="text-xs text-text-3 mb-4 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> {primes.note}</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                {[
                  { label: 'Prime totale (1%)', value: primes.totals?.prime_totale, accent: true },
                  { label: 'Immédiate (0,75%)', value: primes.totals?.prime_immediate },
                  { label: 'Différée (0,25%)', value: primes.totals?.prime_differee },
                ].map((c, i) => (
                  <div key={i} className="rounded-ctrl bg-bg px-4 py-3">
                    <p className="text-[11px] uppercase tracking-[0.08em] font-bold text-label">{c.label}</p>
                    <p className={`font-display text-xl font-semibold tabular-nums ${c.accent ? 'text-orange-text' : 'text-ink'}`}>{fmtEur(c.value)}</p>
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#E6E4DE]">
                      <th className={`text-left ${th} py-2`}>Commercial</th>
                      <th className={`text-right ${th} py-2`}>CA signé</th>
                      <th className={`text-right ${th} py-2`}>Immédiate</th>
                      <th className={`text-right ${th} py-2`}>Différée</th>
                      <th className={`text-right ${th} py-2`}>Totale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(primes.primes || []).map((p) => (
                      <tr key={p.user_id} className="border-b border-divider">
                        <td className="px-3 py-2 font-medium text-ink">{p.name}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-text-2">{fmtEur(p.ca_signed)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-text-2">{fmtEur(p.prime_immediate)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-text-2">{fmtEur(p.prime_differee)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-orange-text">{fmtEur(p.prime_totale)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
